import React, { useState } from 'react';
import {
  ManualClusterPayload, ManualClusterResult, createManualCluster, getCafCluster, getCafCorpusEntry,
} from '../../../api/cafCorpus';
import { FIRST_LABEL_REASON } from '../copy';
import { Icon } from '../Icon';
import { LabelDraft, LabelForm, draftIsValid, draftToWrite, emptyDraft, stanceField, useClaimFieldOptions } from '../LabelForm';
import { useFinalResponseSupported } from '../finalResponseSupport';
import { Modal } from '../Overlay';
import { ReadBack, finishPublication, projectionSuperseded, runSave } from '../saveEngine';
import { useToast } from '../ToastContext';
import { ConflictWarnings, useConflictGate } from './ConflictWarnings';
import { EmailPicker } from './EmailPicker';
import { SenderOption } from './senderOptions';
import { matchesReplacementGuidance } from './guidanceComparison';

interface Props {
  senders: SenderOption[];
  onClose: () => void;
  onCreated: (result: { clusterId: string; labelled: boolean; entryId: string | null }) => void;
  /** `sender` comes with an example, so Teach can open its drawer (the backend lookup needs it). */
  onShow?: (target: { kind: 'group' | 'example'; id: string; sender?: string }) => void;
}

export const GroupByHandModal: React.FC<Props> = ({ senders, onClose, onCreated, onShow }) => {
  const toast = useToast();
  const gate = useConflictGate();
  // Final fix wave C1/C2: an older backend neither asks for nor takes the final-response setting.
  const rules = { finalResponse: useFinalResponseSupported() };
  const claimFields = useClaimFieldOptions();
  const [sender, setSender] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [draft, setDraft] = useState<LabelDraft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const count = picked.length;
  const labelStarted = Boolean(draft.action || draft.why.trim() || draft.finalResponse);
  const labelNow = draftIsValid(draft, rules);
  const blocked = count < 2 || (labelStarted && !labelNow) || saving;

  const create = async () => {
    if (blocked) return;
    const base: ManualClusterPayload = { sender, task_ids: picked };
    const write = labelNow ? draftToWrite(draft, claimFields, undefined, rules) : null;
    const checked = write ? await gate.confirmOrWarn({ source: { kind: 'tasks', ids: picked }, sender, action: write.action, final_response_stance: write.final_response_stance }) : { proceed: true, warnings: [] as string[] };
    if (!checked.proceed) return;
    const payload: ManualClusterPayload = write ? {
      ...base,
      action: write.action,
      note: write.note,
      ...stanceField(write),
      extraction_schema: write.extraction_schema,
      ...(write.categoryOverride ? { categoryOverride: write.categoryOverride } : {}),
      ...(write.archive_override_refs !== undefined ? { archive_override_refs: write.archive_override_refs } : {}),
      expected_version: 0,
      ...(checked.warnings.length ? { acknowledged_warnings: checked.warnings } : {}),
      reason: FIRST_LABEL_REASON,
    } : base;
    setSaving(true);
    setProblem('');
    const result = await runSave<ManualClusterResult>(() => createManualCluster(payload), write ? {
      // The group id is fixed by sender and emails, so creating it again only reads it back.
      readBack: async (): Promise<ReadBack<ManualClusterResult>> => {
        const created = await createManualCluster(base);
        const fresh = await getCafCluster(created.cluster.id);
        const saved = fresh.label_entry;
        if (saved?.version === 1) {
          const entry = await getCafCorpusEntry(saved.id, fresh.sender);
          return entry.version === 1 && matchesReplacementGuidance(entry, {
            action: write.action, note: write.note, reason: FIRST_LABEL_REASON,
            extraction_schema: write.extraction_schema, categoryOverride: write.categoryOverride,
            archive_override_refs: write.archive_override_refs, final_response_stance: write.final_response_stance,
          }) ? { state: 'landed', value: { cluster: created.cluster, entry } } : { state: 'unknown' };
        }
        return saved && saved.version > 1 ? { state: 'unknown' } : { state: 'not_landed' };
      },
      finish: async (value) => (value.entry ? { ...value, entry: await finishPublication(value.entry) } : value),
    } : { replay: () => createManualCluster(base) });
    setSaving(false);
    if (!result.ok) {
      setProblem(result.message);
      toast.failed(result.message, () => { void create(); });
      return;
    }
    const clusterId = result.value?.cluster.id ?? '';
    const entry = result.value?.entry ?? null;
    onCreated({ clusterId, labelled: Boolean(write), entryId: entry?.id ?? null });
    if ((result.value as { creation_status?: string } | null)?.creation_status === 'existing') {
      toast.info('These emails were already a group, so nothing new was made.');
    } else if (write && entry && projectionSuperseded(entry)) {
      // Mirrors GroupDrawer: someone else's version is the one that landed -- still saved, worded plainly.
      toast.saved('Saved. Someone else changed this example at the same time, so their version is the one in use.');
    } else {
      // The spec's save table is binding (final wave item 8): a committed example whose linked
      // records are still pending reads "Saved.", exactly like GroupDrawer -- the link is finished
      // automatically the next time its group opens. No retry action.
      const target = write && entry
        ? { kind: 'example' as const, id: entry.id, sender: entry.sender }
        : clusterId ? { kind: 'group' as const, id: clusterId } : null;
      toast.saved(
        write ? `Saved. New group of ${count} emails created and approved.` : `Saved. New group of ${count} emails added to To label.`,
        target && onShow ? { label: 'Show it', onClick: () => onShow(target) } : undefined,
      );
    }
    onClose();
  };

  const buttonText = count < 2 ? 'Tick at least 2 emails' : labelNow ? `Create and approve (${count} emails)` : `Create group (${count} emails)`;
  return (
    <Modal title="Group emails by hand" size="xwide" onClose={onClose} busy={saving}
      footer={(
        <>
          {problem && <div className="cur-alert cur-alert--error" role="alert"><span className="cur-grow">{problem}</span></div>}
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="ui-btn ui-btn--primary ui-btn--md" disabled={blocked} onClick={() => { void create(); }}>
            {saving ? 'Saving…' : gate.conflicts.length ? 'Save anyway' : buttonText}
          </button>
        </>
      )}>
      <p className="cur-hint">Use this when the overnight grouping missed emails that belong together.</p>
      <EmailPicker mode="many" senders={senders} sender={sender} onSenderChange={setSender} picked={picked}
        onPickedChange={setPicked} disabled={saving} />
      {sender && (
        <details className="cur-more">
          <summary>
            <span className="cur-chev"><Icon name="chev-r" size="sm" /></span>Label it now
            <span className="cur-sum-hint">optional. Leave blank to add it to the To label queue.</span>
          </summary>
          <div className="cur-more-body">
            <LabelForm idPrefix="gbh" value={draft} onChange={setDraft} claimFields={claimFields} showMore={false} disabled={saving} />
            <ConflictWarnings conflicts={gate.conflicts} proposal={{ action: draft.action, final_response_stance: draft.finalResponse }} />
            {labelStarted && !labelNow && (
              <span className="cur-hint">Fill in both to approve now, or clear them to add the group to To label.</span>
            )}
          </div>
        </details>
      )}
    </Modal>
  );
};
